<template>
  <div class="PrepareSignatureSidebar">
    <div v-if="!isTemplate && !isNdaEnvelope" class="PrepareSignatureSidebar-actions">
      <AppTooltip :disabled="canAddSigners">
        <template #activator="{ props }">
          <div v-bind="props">
            <app-button
              :disabled="isReadOnly || !canAddSigners"
              full-width
              type="outlined"
              variant="neutral"
              data-locator="add-signers-button"
              @click="onAddSigners"
            >
              <template #left-icon>
                <app-material-icon name="add" />
              </template>
              {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebar.addSignatories') }}
            </app-button>
          </div>
        </template>
        <div style="max-width: 300px">
          {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebar.overflowSignersLimitTooltip', { recipientsMaxCount: ENVELOPE_RECIPIENTS_MAX_COUNT }) }}
        </div>
      </AppTooltip>
      <app-button
        :disabled="isReadOnly"
        type="outlined"
        variant="neutral"
        full-width
        data-locator="edit-signers-order-button"
        @click="onEditSignersOrder"
      >
        <template #left-icon>
          <app-material-icon name="edit" />
        </template>
        {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebar.editSignersOrder') }}
      </app-button>
      <app-button
        :disabled="isReadOnly"
        type="outlined"
        variant="neutral"
        full-width
        data-locator="auto-tags-button"
        @click="onAddAutoTags"
      >
        <template #left-icon>
          <app-material-icon name="target" />
        </template>
        {{$t('project.signing-checklist.prepare-signature.PrepareSignatureSidebar.automaticTagPlacement')}}
      </app-button>
    </div>
    <div>
      <template v-if="hasSignersGroup">
        <div>
          <div class="PrepareSignatureSidebar-section">
            <span class="text--small text--bold text-text-primary">
              {{ hasCommonSigners ? $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebar.commonSigners') : $t('common.signer', 2) }}
            </span>
          </div>
          <template v-for="(signerGroup, groupIndex) in signersGroups" :key="`title-${groupIndex}`">
            <p
              v-if="signersGroups.length > 1"
              class="text--small text--bold pl-4 pt-4 text-text-secondary"
            >
              {{ `${$t('common.group')} #${groupIndex + 1}` }}
            </p>

            <v-expansion-panels
              :model-value="openedSignerPanelIndex"
              :readonly="isReadOnly"
              :disabled="isReadOnly"
              flat
              variant="accordion"
            >
              <template v-for="signer in signerGroup" :key="signer.signatureId">
                <PrepareSignatureSidebarSigner
                  :is-nda-envelope="isNdaEnvelope"
                  :delete-signer-pending-ids="deleteSignerPendingIds"
                  :drag-active="false"
                  :index="getSignerIndex(signer.signatureId)"
                  :is-multiple-envelope="isMultipleEnvelope"
                  :is-read-only="isReadOnly"
                  :is-template="isTemplate"
                  :order="groupIndex"
                  :provider="provider"
                  :signer="signer"
                  @change-signer="onChangeSigner"
                  @delete-signer="onDeleteSigner"
                  @rename-signer="onRenameSigner"
                  @update-step-signers="onUpdateStepSigners"
                />

                <template v-if="signer.hasWitness">
                  <PrepareSignatureSidebarSigner
                    v-for="witness in getWitnessesForSigner(signer.signatureId)"
                    :key="witness.signatureId"
                    :is-nda-envelope="isNdaEnvelope"
                    :delete-signer-pending-ids="deleteSignerPendingIds"
                    :drag-active="false"
                    :index="getSignerIndex(signer.signatureId)"
                    :is-multiple-envelope="isMultipleEnvelope"
                    :is-read-only="isReadOnly"
                    :is-template="isTemplate"
                    :order="groupIndex"
                    :provider="provider"
                    :signer="witness"
                    isWitness
                    @change-signer="onChangeSigner"
                    @delete-signer="onDeleteSigner"
                    @update-step-signers="onUpdateStepSigners"
                  />
                </template>
              </template>
            </v-expansion-panels>
          </template>
        </div>
      </template>
    </div>
    <div class="pa-4">
      <app-button
        :disabled="isReadOnly"
        type="outlined"
        variant="neutral"
        full-width
        data-locator='delete-all-tags-button'
        @click="onDeleteAllTags"
      >
        {{ $t('project.signing-checklist.prepare-signature.PrepareSignatureSidebar.deleteAllTagsButtonLabel') }}
      </app-button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { ENVELOPE_RECIPIENTS_MAX_COUNT } from '@/common/enveloppe/envelope-recipients'
import { getCommonAndUniqueSignerIdsFromSignersGroup } from '@/common/utils/signingChecklist'
import { ENVELOPE_TYPES, SIGNATURE_TYPE_HANDWRITTEN } from '@/project/signing-checklist/constants'
import { GET_TAGGING_INFORMATION } from '@/store/modules/prepareSignature/action_types'

import PrepareSignatureSidebarSigner from './PrepareSignatureSidebarSigner.vue'

export default defineComponent({
    name: 'PrepareSignatureSidebar',
    components: { AppTooltip, PrepareSignatureSidebarSigner },
    props: {
      deleteSignerPendingIds: {
        type: Array,
        required: true,
      },
      envelopeId: {
        type: Number,
        required: true,
      },
      envelopeType: {
        type: String,
        required: true,
      },
      isReadOnly: {
        type: Boolean,
      },
      provider: {
        type: String,
        required: true,
      },
      signatureType: {
        type: String,
        required: true,
      },
      signers: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        ENVELOPE_RECIPIENTS_MAX_COUNT,
      }
    },
    computed: {
      hasSignersGroup () {
        return this.signersGroups.length > 0
      },
      hasCommonSigners () {
        return this.hasSignersGroup && this.isMultipleEnvelope && this.signersGroups.flat().some((signer) => signer.signatureType === 'single')
      },
      isMultipleEnvelope () {
        return this.envelopeType === ENVELOPE_TYPES.MULTIPLE
      },
      isNdaEnvelope () {
        return this.envelopeType === ENVELOPE_TYPES.NDA
      },
      isSignersOrderButtonDisplayed () {
        return !this.isMultipleEnvelope && this.signatureType !== SIGNATURE_TYPE_HANDWRITTEN && !this.isTemplate
      },
      isTemplate () {
        return this.$route.path.includes('envelope-template') // TO OPTIMIZE
      },
      openedSignerPanelIndex () {
        return this.isReadOnly ? undefined : 0
      },
      signersGroups () {
        return this.signers.map(signersGroup => {
          signersGroup.map((signer) => {
            if (signer.signatureType !== 'witness') {
              signer.hasWitness = signersGroup.some((s) => s.witnessFor === signer.signatureId)
            }
          })
          const commonSigners = signersGroup.filter(signer => signer.signatureType === 'single')
          const uniqueSigners = signersGroup.filter(signer => signer.signatureType === 'multiple')

          return [...commonSigners, ...uniqueSigners]
        })
      },
      canAddSigners () {
        let currentSignersCount = this.signersGroups.flat().filter(signer => signer.signatureType === 'single').length

        const multipleSigners = this.signersGroups.flat().find(signer => signer.signatureType === 'multiple')
        if (multipleSigners) {
          currentSignersCount += multipleSigners.signers.length
        }

        return currentSignersCount < ENVELOPE_RECIPIENTS_MAX_COUNT
      },
    },
    async mounted () {
      if (!this.signers) {
        this.getTags()
      }
    },
    methods: {
      ...mapActions('prepareSignature', [GET_TAGGING_INFORMATION]),
      getTags () {
        this.$emit('get-tags')
      },
      onAddAutoTags () {
        this.$emit('add-auto-tags')
      },
      onAddSigners () {
        this.$emit('add-signers')
      },
      onChangeSigner (signer) {
        this.$emit('change-signer', signer)
      },
      onDeleteAllTags () {
        this.$emit('delete-all-tags')
      },
      onDeleteSigner (signatureId) {
        this.$emit('delete-signer', signatureId)
      },
      onEditSignersOrder () {
        this.$emit('edit-signers-order')
      },
      onRenameSigner (signerData) {
        this.$emit('rename-signer', signerData)
      },
      scrollTo (page) {
        this.$emit('scroll-to', page)
      },
      getSignerIndex (signatureId) {
        return this.signersGroups.flat().findIndex(signer => signer.signatureId === signatureId)
      },
      async onUpdateStepSigners () {
        await this.GET_TAGGING_INFORMATION({
          mnemo: this.$route.params.mnemo,
          envId: this.envelopeDisplayInformation.envelopeId,
        })
        const signersDescription = getCommonAndUniqueSignerIdsFromSignersGroup(this.signersGroups)
        this.step.objectToPost['signers'] = signersDescription.commonSigners
        if (this.step.objectToPost.envelope.envelopeType === ENVELOPE_TYPES.MULTIPLE) {
          this.step.objectToPost['uniqueSigners'] = signersDescription.uniqueSigners
        }
      },
      getWitnessesForSigner (signerId) {
        return this.signers.flat()
          .filter(signer => signer.signatureType === 'witness' && signer.witnessFor === signerId)
      },
    },
})
</script>

<style lang="scss" scoped>
.PrepareSignatureSidebar {
  border-bottom-left-radius: 8px;
}

.PrepareSignatureSidebar-section {
  padding: 16px 0 0 16px;
}

.PrepareSignatureSidebar-actions {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding: 16px;
  border-bottom: 1px solid rgb(var(--v-theme-border-primary));
}

:deep(.v-expansion-panel .v-expansion-panel-title__overlay) {
  background-color: transparent !important;
}
</style>
